import { createContext, ReactNode, useContext, useState } from 'react';

import AlertModal from '@/components/modal/AlertModal';
import { useTranslation } from 'react-i18next';

interface AlertTypeProps {
  message: string;
  onConfirm?: () => void;
  buttonText?: string;
  xButton?: boolean;
}
interface AlertContextType {
  showAlert: ({
    message,
    onConfirm,
    buttonText,
    xButton,
  }: AlertTypeProps) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const AlertProvider = ({ children }: { children: ReactNode }) => {
  const [message, setMessage] = useState<string>('');
  const [onConfirm, setOnConfirm] = useState<() => void>(() => {});
  const [visible, setVisible] = useState<boolean>(false);
  const [buttonText, setButtonText] = useState<string | null>(null);
  const [xButton, setXbutton] = useState<boolean>(false);
  const { t } = useTranslation();

  const showAlert = ({
    message,
    onConfirm,
    buttonText,
    xButton,
  }: AlertTypeProps) => {
    setMessage(message);
    setOnConfirm(() => onConfirm || (() => {}));
    setVisible(true);
    if (xButton) {
      setXbutton(xButton);
    }
    if (buttonText) {
      setButtonText(buttonText);
    }
  };

  const handleClose = (isConfirmButtonClick = false) => {
    // 상태 초기화 전에 현재 onConfirm 저장
    const confirmCallback = onConfirm;

    // 모달 상태 초기화
    setVisible(false);
    setMessage('');
    setButtonText(null);
    setOnConfirm(() => () => {});
    setXbutton(false);

    if (isConfirmButtonClick) {
      confirmCallback();
    }
  };
  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      {visible && (
        <AlertModal
          message={message}
          onClose={(isConfirmButtonClick) => handleClose(isConfirmButtonClick)}
          buttonText={buttonText ? buttonText : t('confirm')}
          xButton={xButton}
        />
      )}
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context)
    throw new Error('useAlert must be used within an AlertProvider');
  return context;
};
